import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

// Импорты сервисов и типов
import { api } from '../../services/api';
import { LocationPicker } from '../map';
import type { Device, DeviceUpdate, GeoLocation } from '../../types';

interface DeviceEditDialogProps {
  open: boolean;
  device: Device;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

interface FormData {
  name: string;
  description: string;
  location: string;
  floor: string;
  room: string;
  screen_resolution: string;
  screen_orientation: 'landscape' | 'portrait';
  heartbeat_interval: number;
  is_active: boolean;
  auto_update: boolean;
  maintenance_mode: boolean;
  
  // Геолокация
  geolocation: GeoLocation | null;
  country: string;
  city: string;
  full_address: string;
  location_id: string;
}

const screenResolutions = [
  '1920x1080',
  '1366x768',
  '1280x1024',
  '1280x720',
  '1024x768',
  '3840x2160',
  '2560x1440',
];

export const DeviceEditDialog: React.FC<DeviceEditDialogProps> = ({
  open,
  device,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    location: '',
    floor: '',
    room: '',
    screen_resolution: '',
    screen_orientation: 'landscape',
    heartbeat_interval: 60,
    is_active: true,
    auto_update: true,
    maintenance_mode: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Заполнение формы данными устройства при открытии
  useEffect(() => {
    if (open && device) {
      // Создание объекта геолокации из данных устройства
      const geolocation: GeoLocation | null = (device.latitude && device.longitude) ? {
        latitude: device.latitude,
        longitude: device.longitude,
        country: device.country,
        city: device.city,
        full_address: device.full_address,
        location_id: device.location_id
      } : null;

      setFormData({
        name: device.name || '',
        description: device.description || '',
        location: device.location || '',
        floor: device.floor || '',
        room: device.room || '',
        screen_resolution: device.screen_resolution || '',
        screen_orientation: device.screen_orientation || 'landscape',
        heartbeat_interval: device.heartbeat_interval || 60,
        is_active: device.is_active ?? true,
        auto_update: device.auto_update ?? true,
        maintenance_mode: device.maintenance_mode ?? false,
        
        // Геолокация
        geolocation,
        country: device.country || '',
        city: device.city || '',
        full_address: device.full_address || '',
        location_id: device.location_id || '',
      });
      setErrors({});
    }
  }, [open, device]);

  // Мутация для обновления устройства
  const updateDeviceMutation = useMutation({
    mutationFn: (deviceData: DeviceUpdate) => api.updateDevice(device.id, deviceData),
    onSuccess: (data) => {
      onSuccess(t('devices.deviceUpdated', { name: data.name }));
      onClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.detail || t('devices.errorUpdatingDevice'),
        { variant: 'error' }
      );
    },
  });

  // Обработчик изменения формы
  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Очистка ошибки при изменении поля
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Обработчик изменения геолокации
  const handleGeolocationChange = (location: GeoLocation | null) => {
    setFormData(prev => ({ 
      ...prev, 
      geolocation: location,
      // Синхронизация с отдельными полями
      country: location?.country || '',
      city: location?.city || '',
      full_address: location?.full_address || '',
      location_id: location?.location_id || ''
    }));
  };

  // Валидация формы
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }

    if (formData.heartbeat_interval < 10 || formData.heartbeat_interval > 300) {
      newErrors.heartbeat_interval = t('validation.heartbeatInterval');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Обработчик отправки формы
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const deviceData: DeviceUpdate = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      location: formData.location.trim() || undefined,
      floor: formData.floor.trim() || undefined,
      room: formData.room.trim() || undefined,
      screen_resolution: formData.screen_resolution || undefined,
      screen_orientation: formData.screen_orientation,
      heartbeat_interval: formData.heartbeat_interval,
      is_active: formData.is_active,
      auto_update: formData.auto_update,
      maintenance_mode: formData.maintenance_mode,
      
      // Геолокация
      latitude: formData.geolocation?.latitude,
      longitude: formData.geolocation?.longitude,
      country: formData.geolocation?.country || formData.country.trim() || undefined,
      city: formData.geolocation?.city || formData.city.trim() || undefined,
      full_address: formData.geolocation?.full_address || formData.full_address.trim() || undefined,
      location_id: formData.geolocation?.location_id || formData.location_id.trim() || undefined,
    };

    updateDeviceMutation.mutate(deviceData);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('devices.editDevice')}: {device.name}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Основная информация */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary">
              {t('devices.basicInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.name')}
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.type')}
              value={device.device_type || device.type || 'N/A'}
              disabled
              helperText={t('devices.typeCannotBeChanged')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('devices.description')}
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={2}
            />
          </Grid>

          {/* Местоположение */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary" sx={{ mt: 2 }}>
              {t('devices.location')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('devices.location')}
              value={formData.location}
              onChange={handleChange('location')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('devices.floor')}
              value={formData.floor}
              onChange={handleChange('floor')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('devices.room')}
              value={formData.room}
              onChange={handleChange('room')}
            />
          </Grid>

          {/* Геолокация */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mt: 1 }}>
              {t('map.geolocation')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <LocationPicker
              value={formData.geolocation}
              onChange={handleGeolocationChange}
              label={t('map.deviceLocation')}
              placeholder={t('map.selectOnMap')}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('map.locationId')}
              value={formData.location_id}
              onChange={handleChange('location_id')}
              helperText={t('devices.locationIdHelper')}
            />
          </Grid>

          {/* Дополнительные поля адреса для ручного ввода */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('map.country')}
              value={formData.country}
              onChange={handleChange('country')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('map.city')}
              value={formData.city}
              onChange={handleChange('city')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('map.fullAddress')}
              value={formData.full_address}
              onChange={handleChange('full_address')}
              multiline
              rows={2}
            />
          </Grid>

          {/* Техническая информация (только для чтения) */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary" sx={{ mt: 2 }}>
              {t('devices.technicalInfo')} 
              <Typography component="span" variant="body2" color="text.secondary">
                ({t('devices.readOnly')})
              </Typography>
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.deviceUuid')}
              value={device.device_uuid || device.uuid || 'N/A'}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.ipAddress')}
              value={device.ip_address || 'N/A'}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.macAddress')}
              value={device.mac_address || 'N/A'}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.hostname')}
              value={device.hostname || 'N/A'}
              disabled
            />
          </Grid>

          {/* Конфигурация экрана */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary" sx={{ mt: 2 }}>
              {t('devices.screenConfiguration')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.screenResolution')}</InputLabel>
              <Select
                value={formData.screen_resolution}
                label={t('devices.screenResolution')}
                onChange={handleChange('screen_resolution')}
              >
                <MenuItem value="">
                  <em>{t('common.none')}</em>
                </MenuItem>
                {screenResolutions.map((resolution) => (
                  <MenuItem key={resolution} value={resolution}>
                    {resolution}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.screenOrientation')}</InputLabel>
              <Select
                value={formData.screen_orientation}
                label={t('devices.screenOrientation')}
                onChange={handleChange('screen_orientation')}
              >
                <MenuItem value="landscape">{t('devices.orientation.landscape')}</MenuItem>
                <MenuItem value="portrait">{t('devices.orientation.portrait')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Настройки устройства */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary" sx={{ mt: 2 }}>
              {t('devices.deviceSettings')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('devices.heartbeatInterval')}
              type="number"
              value={formData.heartbeat_interval}
              onChange={handleChange('heartbeat_interval')}
              error={!!errors.heartbeat_interval}
              helperText={errors.heartbeat_interval || t('devices.heartbeatIntervalHelper')}
              InputProps={{
                endAdornment: <Typography variant="body2" color="text.secondary">{t('common.seconds')}</Typography>
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.status')}</InputLabel>
              <Select
                value={device.status || 'offline'}
                label={t('devices.status')}
                disabled
              >
                <MenuItem value="online">🟢 {t('devices.status.online')}</MenuItem>
                <MenuItem value="offline">🔴 {t('devices.status.offline')}</MenuItem>
                <MenuItem value="maintenance">🟡 {t('devices.status.maintenance')}</MenuItem>
                <MenuItem value="error">❌ {t('devices.status.error')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Дополнительные настройки */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom color="primary" sx={{ mt: 2 }}>
              {t('devices.additionalSettings')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.activeStatus')}</InputLabel>
              <Select
                value={formData.is_active ? 'active' : 'inactive'}
                label={t('devices.activeStatus')}
                onChange={(e) => handleChange('is_active')({ target: { value: e.target.value === 'active' } })}
              >
                <MenuItem value="active">✅ {t('devices.active')}</MenuItem>
                <MenuItem value="inactive">⏸️ {t('devices.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.autoUpdate')}</InputLabel>
              <Select
                value={formData.auto_update ? 'enabled' : 'disabled'}
                label={t('devices.autoUpdate')}
                onChange={(e) => handleChange('auto_update')({ target: { value: e.target.value === 'enabled' } })}
              >
                <MenuItem value="enabled">🔄 {t('devices.autoUpdateEnabled')}</MenuItem>
                <MenuItem value="disabled">⏹️ {t('devices.autoUpdateDisabled')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('devices.maintenanceMode')}</InputLabel>
              <Select
                value={formData.maintenance_mode ? 'enabled' : 'disabled'}
                label={t('devices.maintenanceMode')}
                onChange={(e) => handleChange('maintenance_mode')({ target: { value: e.target.value === 'enabled' } })}
              >
                <MenuItem value="disabled">✅ {t('devices.maintenanceModeOff')}</MenuItem>
                <MenuItem value="enabled">🔧 {t('devices.maintenanceModeOn')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {updateDeviceMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {updateDeviceMutation.error?.message || t('devices.errorUpdatingDevice')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={updateDeviceMutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateDeviceMutation.isPending}
          startIcon={updateDeviceMutation.isPending ? <CircularProgress size={16} /> : <EditIcon />}
        >
          {updateDeviceMutation.isPending ? t('common.updating') : t('devices.updateDevice')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 